import React from 'react';
import styled from 'styled-components';
import { observer } from 'mobx-react-lite';
import { TrashIcon } from '@radix-ui/react-icons';
import { useNavigate } from 'react-router-dom';
import { useSortable } from '@dnd-kit/react/sortable';

import { Project, Task } from '../../../../app/entities';
import { StatusSelector } from '../../../process/components/status-selector';
import { PriorityIcon, PrioritySelector } from '../../../process/components/priority-selector';
import { AssigneeSelector } from '../../../../components/assignee-selector/assignee-selector';
import { ContextMenu } from '../../../../components/modals/context-menu/ContextMenu';
import { DeleteDialog } from '../../../../components/dialogs/DeleteDialog';
import { KanbanItemCard } from '../../../kanban/components/kanban-item-card';
import { useCurrentMember } from '../../../../hooks/use-current-member';
import { authStore } from '../../../../core/stores/auth-store';
import { openModal } from '../../../../core/modal/open-modal';
import { TaskDateSelector } from '../task-date-selector';
import { toLegacyInstant } from '../../../../core/services/plain-date';
import { TagsViewer } from '../tags-viewer';

import styles from '../task-item.module.scss';

type TaskCardProps = {
  taskId: string;
  index: number;
  columnId: string;
  dragDisabled?: boolean;

  /**
   * Override the default navigation when clicking on the card
   */
  onSelect?: (taskId: string) => void;
};

/**
 * Interactive summary card for the kanban board. Reuses the shared
 * `KanbanItemCard` shell (same card as the project-overview step card) and the
 * DnD wiring of `step-item.tsx`. The status / priority / assignee / due date /
 * tags controls edit the task in place (same handlers as the list row);
 * clicking elsewhere on the card opens the task detail.
 */
export const TaskCard: React.FC<TaskCardProps> = observer(
  ({ taskId, index, columnId, dragDisabled, onSelect }) => {
    const navigate = useNavigate();
    const currentMember = useCurrentMember();
    const task = Task.getOne(taskId) as Task;

    // When drag is disabled (tag grouping) a task can appear in several columns,
    // so the sortable id must be unique per column to avoid registry collisions.
    // Draggable groupings always map a task to a single column, so the plain
    // task id is kept there — it must match the id stored in the board state for
    // `move()` to work.
    const { ref, isDragging } = useSortable({
      id: dragDisabled ? `${columnId}:${taskId}` : taskId,
      index,
      type: 'item',
      accept: 'item',
      group: columnId,
      disabled: dragDisabled,
    });

    if (!task) {
      return null;
    }

    const taskProject = task.projectId ? Project.getOne(task.projectId) : undefined;

    // Same rule as the list row: guests can only edit tasks assigned to them.
    const isViewOnly =
      !currentMember || (currentMember.role === 'guest' && currentMember._id !== task.assigneeId);

    const handleClick = () => {
      if (onSelect) {
        onSelect(task._id);
      } else {
        navigate(`./${task._id}`);
      }
    };

    // Same delete flow as the list row's kebab menu (`task-item.tsx`).
    const handleOpenDelete = () => {
      if (isViewOnly) {
        return authStore.setAuthorized(false);
      }

      openModal(DeleteDialog, {
        title: 'Delete task',
        text: `The task will be deleted completely from the project.`,
        onSubmit: () => {
          task.delete();
        },
      });
    };

    const contextMenuItems: React.ComponentProps<typeof ContextMenu>['items'] = [
      {
        title: 'Delete task',
        icon: <TrashIcon />,
        type: 'danger' as const,
        onSelect: handleOpenDelete,
      },
    ];

    return (
      <ContextMenu items={contextMenuItems}>
        <KanbanItemCard
          ref={ref}
          status={task.status}
          isDragging={isDragging}
          onClick={handleClick}
          title={task.title}
          leading={
            // `display: contents` keeps the step-card layout while letting the
            // control's clicks stop short of the card's navigate handler.
            <NoNavigate onClick={(e) => e.stopPropagation()}>
              <StatusSelector
                status={task.status}
                statusMap={Task.StatusMapper}
                isViewOnly={isViewOnly}
                disableTooltip={isViewOnly}
                onChange={(status) => task.update({ status })}
              />
            </NoNavigate>
          }
          trailing={
            <NoNavigate onClick={(e) => e.stopPropagation()}>
              <AssigneeSelector
                isViewOnly={isViewOnly}
                project={taskProject}
                selectedMemberId={task.assigneeId}
                onChange={(assigneeId) =>
                  task.update({ assigneeId: assigneeId || null, projectId: task.projectId })
                }
                iconOnly
              />
            </NoNavigate>
          }
          footer={
            <>
              <NoNavigate onClick={(e) => e.stopPropagation()}>
                <PrioritySelector
                  isViewOnly={isViewOnly}
                  priority={task.priority}
                  onChange={(priority) => task.update({ priority })}
                  trigger={
                    <div className={styles.priority}>
                      <PriorityIcon priority={task.priority} data-disabled={isViewOnly} />
                    </div>
                  }
                />
              </NoNavigate>

              <NoNavigate onClick={(e) => e.stopPropagation()}>
                <TaskDateSelector
                  isViewOnly={isViewOnly}
                  date={task.dueDate}
                  status={task.status}
                  onUpdate={(date) =>
                    // `Task` keeps instant semantics (its own follow-up ticket): its API path
                    // parses a bare `YYYY-MM-DD` as server-zone midnight, so the picked day goes
                    // back as the legacy local-noon instant. `null` clears it, forwarded as is.
                    task.update({ dueDate: date && toLegacyInstant(date) })
                  }
                />
              </NoNavigate>

              <Tags onClick={(e) => e.stopPropagation()}>
                <TagsViewer
                  targetId={task._id}
                  targetType="task"
                  overflow="collapse"
                  alignment="end"
                />
              </Tags>
            </>
          }
        />
      </ContextMenu>
    );
  },
);

const NoNavigate = styled.div`
  display: contents;
`;

const Tags = styled.div`
  flex: 1;
  min-width: 0;
  display: flex;
  justify-content: flex-end;
`;
